import React, { useEffect, useState, useCallback, useRef } from "react";
import qs from "qs";
import { Link, useParams, useHistory, useLocation } from "react-router-dom";

import { useDispatch, useStore } from "../../store";
import * as actions from "../../store/viewmodel/actions";
import * as aToZBrowseFunctions from "./functions";
import {
  logBrowseTabClicked,
  logGenericBrowseTopicClicked,
} from "../../util/analytics";

import {
  MainSpinner,
  NarrowFilter,
  Page,
  PageDescription,
  PageTitle,
  PopulationFilter,
} from "../../components";
import {
  AZBrowseHeader,
  AZBrowseSticky,
  AZLogo,
  AnchorWithHighlights,
} from "./_.styled";
import {
  AlphaTab,
  AlphaTabs,
  Divider,
  Flex,
  List,
  ListItem,
  Tab,
  TabContext,
  TabList,
  Type,
} from "discover.medical.react-component-library";
import useStickyElement from "../../hooks/useStickyElement";
import { scrollToElement } from "../../util/viewport-utils";
import { setQueryStringAndRetainHash } from "../../util/queryParamUtils";

const AtoZBrowse = () => {
  const store = useStore();
  const {
    viewmodel: {
      isLoading,
      title,
      description,
      logo,
      navigation,
      contents,
      noResultsMessage,
      filteredList,
      filteredAlphaTabs,
      shouldRetrieveAllContentForPubType,
    },
  } = store;
  const { tabs, description: navDescription, alphaTabs, filters } =
    navigation || {};
  const { slug } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [activeAlphaTab, setActiveAlphaTab] = useState("");
  const [searchKey, setSearchKey] = useState(undefined);
  const [selectedPopulation, setSelectedPopulation] = useState(undefined);
  const { isElementSticky } = useStickyElement();
  const stickyWrapperId = "sticky-az-wrapper";
  const rootElementId = `${slug}-browse-root`;
  const hasFetchedViewModelOnPageLoadRef = useRef(false);

  const resetView = useCallback((slug) => {
    setActiveAlphaTab(undefined);
    setActiveTabIndex(
      tabs?.findIndex((tab) => tab.route.includes(slug))
    );
    setSearchKey(undefined);
    setSelectedPopulation(undefined);
    actions.getViewmodel(dispatch, slug, location.hash?.substring(1));
  }, [dispatch, location.hash, tabs]);

  useEffect(() => {
    if (hasFetchedViewModelOnPageLoadRef.current) {
      return;
    }
    setActiveAlphaTab("");
    setSearchKey(undefined);
    setSelectedPopulation(undefined);
    if (!shouldRetrieveAllContentForPubType) {
      actions.getViewmodel(dispatch, slug, location.hash?.substring(1));
      hasFetchedViewModelOnPageLoadRef.current = true;
    }
  }, [dispatch, slug, location.hash, shouldRetrieveAllContentForPubType]);
  useEffect(() => {
    if (tabs?.length > 0) {
      setActiveTabIndex(tabs.findIndex((tab) => tab.route.includes(slug)));
      setSearchKey(undefined);
      setSelectedPopulation(undefined);
      if (!location.hash?.substring(1)) {
        setActiveAlphaTab(filteredAlphaTabs?.[0]?.label);
      }
      actions.resetFilteredList(dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs, slug]);
  useEffect(() => {
    if (location?.hash?.substring(1) && !activeAlphaTab && filteredAlphaTabs) {
      const rootElement = document.getElementById(rootElementId);
      const distance = rootElement.getBoundingClientRect().top;
      const scrollOffset = distance >= 0 ? distance : 0;
      setActiveAlphaTab(location.hash.substring(1));
      scrollToElement(
        location.hash.substring(1),
        stickyWrapperId,
        scrollOffset
      );
    } else if (
      location?.hash?.substring(1) &&
      !activeAlphaTab &&
      filteredAlphaTabs?.length > 0
    ) {
      setActiveAlphaTab(filteredAlphaTabs[0].label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, location.hash, activeAlphaTab, filteredAlphaTabs]);

  useEffect(() => {
    const qsParams = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });
    let searchKeyToUse = qsParams.searchKey || undefined;
    let populationTypeToUse = qsParams.populationType || undefined;
    setSearchKey(searchKeyToUse);
    setSelectedPopulation(populationTypeToUse);
    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    if (searchKeyToUse || populationTypeToUse) {
      const newQsParams = {
        searchKey: searchKeyToUse,
        populationType: populationTypeToUse,
      };

      const qsValue = qs.stringify(
        {
          ...qsParams,
          ...newQsParams,
        },
        { addQueryPrefix: true }
      );
      const newUrl = `${location.pathname}${qsValue}${location.hash}`;
      if (newUrl !== currentUrl) {
        history.replace(newUrl, location.state);
      }

      aToZBrowseFunctions.filterList(
        contents,
        alphaTabs,
        searchKeyToUse,
        populationTypeToUse,
        dispatch,
        shouldRetrieveAllContentForPubType
      );
    }
  }, [
    history,
    location,
    searchKey,
    contents,
    alphaTabs,
    selectedPopulation,
    dispatch,
    shouldRetrieveAllContentForPubType,
  ]);
  useEffect(() => {
    const activeAlphaTabMismatch = !location.hash?.substring(1) && !!activeAlphaTab && activeAlphaTab !== filteredAlphaTabs?.[0]?.label;
    const searchKeyMismatch = !!searchKey && !location.search;
    const activeTabMismatch = !!tabs && tabs[activeTabIndex]?.route?.split("/")[2] !== slug;

    if (history.action === "PUSH") {
      if (activeAlphaTabMismatch || searchKeyMismatch || activeTabMismatch) {
        resetView(slug);
      }
    }
  }, [
    location,
    resetView,
    activeAlphaTab,
    activeTabIndex,
    filteredAlphaTabs,
    searchKey,
    slug,
    tabs,
    history
  ]);

  const handleTabClick = (telemetryData, clickedTabRoute) => {
    const clickedTabSlug = clickedTabRoute.split("/")[2];
    logBrowseTabClicked(telemetryData);
    resetView(clickedTabSlug);
  };

  const renderContentLinks = () => {
    return filteredList?.length > 0 ? (
      filteredList?.map((alphaTabContent, index) => {
        return (
          alphaTabContent && (
            <div key={index}>
              {alphaTabContent.label && (
                <Type
                  element="h1"
                  id={alphaTabContent.label}
                  marginBottom="md"
                  fontSize="xxl"
                  data-testid={`alphaTabContentLabel-${alphaTabContent.label}`}
                >
                  {alphaTabContent.label}
                </Type>
              )}
              <List type="ul" noIndicator>
                {alphaTabContent.results?.map((item) => (
                  <ListItem key={item.route}>
                    <AnchorWithHighlights
                      linkComponent={Link}
                      href={item.route}
                      onClick={(e) => {
                        e.preventDefault();
                        history.push(item.route);
                        logGenericBrowseTopicClicked({
                          ...item.telemetryData,
                          characterFrom: activeAlphaTab,
                        });
                      }}
                    >
                      <span
                        dangerouslySetInnerHTML={{
                          __html: item.label?.replace(/\s/g, "&nbsp;"),
                        }}
                      />
                    </AnchorWithHighlights>
                  </ListItem>
                ))}
              </List>
            </div>
          )
        );
      })
    ) : (
      <Flex>
        <Type element="em" marginTop="lg">
          {noResultsMessage}
        </Type>
      </Flex>
    );
  };

  return (
    <Page id={`${slug}-browse-root`} data-testid={`${slug}-browse-root`}>
      <AZBrowseHeader container data-testid={`${slug}-browse-header`}>
        <Flex cell column>
          <PageTitle>{title}</PageTitle>
          {description && <PageDescription>{description}</PageDescription>}
        </Flex>
        {logo && 
          <Flex align={{content: "center"}}>
            <AZLogo src={logo.src} alt={logo.alt}/>
          </Flex>
        }
      </AZBrowseHeader>
      <AZBrowseSticky $isStuck={isElementSticky} id={stickyWrapperId}>
        {tabs?.length > 0 && (
          <TabContext activeTab={activeTabIndex}>
            <TabList aria-label={title}>
              {tabs.map(({ id, label, route, telemetryData }) => (
                <Tab
                  key={id}
                  label={label}
                  linkComponent={Link}
                  to={{
                    pathname: route,
                  }}
                  onClick={() => handleTabClick(telemetryData, route)}
                />
              ))}
            </TabList>
            <Divider noMargin />
          </TabContext>
        )}
        {navDescription && (
          <PageDescription
            display={isElementSticky ? "none" : "block"}
            marginTop="md"
            marginBottom="sm"
          >
            {navDescription}
          </PageDescription>
        )}
        {filteredAlphaTabs?.length > 0 && (
          <AlphaTabs scrollButtons>
            {filteredAlphaTabs.map((alphaTab) => (
              <AlphaTab
                key={alphaTab.label}
                data-testid={`alphaTab-${alphaTab.label}`}
                active={alphaTab.label === activeAlphaTab}
                onClick={(e) =>
                  aToZBrowseFunctions[alphaTab.function](
                    e,
                    dispatch,
                    alphaTab,
                    activeAlphaTab,
                    history,
                    location,
                    setActiveAlphaTab,
                    slug,
                    rootElementId,
                    stickyWrapperId,
                    setSearchKey
                  )
                }
                linkComponent={Link}
                href={alphaTab.route}
              >
                {alphaTab.label}
              </AlphaTab>
            ))}
          </AlphaTabs>
        )}
        {filters?.length > 0 && (
          <Flex container marginTop="md">
            {filters.map((filter) => {
              switch (filter.type) {
                case "input":
                  return (
                    <NarrowFilter
                      key={filter.queryParam}
                      data-testid={`filter-${filter.queryParam}`}
                      value={searchKey}
                      placeholder={filter.placeholder.replace(
                        "%%LETTER%%",
                        activeAlphaTab
                      )}
                      onChange={(e) => {
                        const searchKeyValue =
                          e.currentTarget.value === ""
                            ? undefined
                            : e.currentTarget.value;
                        setSearchKey(searchKeyValue);
                        setQueryStringAndRetainHash(history, [
                          "searchKey",
                          searchKeyValue,
                        ]);
                        aToZBrowseFunctions.filterList(
                          contents,
                          alphaTabs,
                          searchKeyValue,
                          selectedPopulation,
                          dispatch,
                          shouldRetrieveAllContentForPubType
                        );
                      }}
                    />
                  );
                case "select":
                  return (
                    <PopulationFilter
                      options={filter.options}
                      handleChange={(e) => {
                        const populationValueToSet =
                          e === 0 ? undefined : filter.options.at(e);
                        setSelectedPopulation(populationValueToSet);
                        setQueryStringAndRetainHash(history, [
                          "populationType",
                          populationValueToSet,
                        ]);
                        aToZBrowseFunctions.filterList(
                          contents,
                          alphaTabs,
                          searchKey,
                          populationValueToSet,
                          dispatch,
                          shouldRetrieveAllContentForPubType
                        );
                      }}
                    />
                  );
                default:
                  return <></>;
              }
            })}
          </Flex>
        )}
      </AZBrowseSticky>
      {isLoading ? <MainSpinner /> : renderContentLinks()}
    </Page>
  );
};

AtoZBrowse.displayName = "AtoZBrowse";

export default AtoZBrowse;
